import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Button, Flex, Spin, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { getAuth } from 'firebase/auth';
import { useMemo } from 'react';

import {
  getGetAccountQueryKey,
  getGetAccountQueryOptions,
  useAcceptAccountInvitation,
  useGetAccountInvitation,
  useRejectAccountInvitation,
} from '../../api';
import { signOutWithRevoke } from '../../modules/auth/sign-out';
import { CenteredLayout, CenteredLayoutContent } from '../../ui/components/layout';
import { TextTertiary } from '../../ui/components/text';
import { hideHtmlLoader } from '../../ui/utils/html';

const InvitationRoute = () => {
  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const { invitationId } = Route.useParams();
  const { account } = Route.useRouteContext();

  const invitationFromAccount = useMemo(
    () => account?.invitations?.find(({ id }) => id === invitationId),
    [account?.invitations, invitationId],
  );

  const {
    data: invitationRes,
    isLoading: previewLoading,
    isError: previewError,
  } = useGetAccountInvitation(invitationId, { query: { enabled: !invitationFromAccount } });

  const display = invitationFromAccount ?? invitationRes?.data;
  const canAct = !!display && account.email === display.email;

  const acceptMutation = useAcceptAccountInvitation(
    {
      mutation: {
        onSuccess: async () => {
          message.success('Invitation accepted');

          await getAuth().currentUser?.getIdToken(true);
          await queryClient.resetQueries({ queryKey: getGetAccountQueryKey() });

          navigate({ to: '/' });
        },
        onError: () => {
          message.error('Failed to accept invitation');
        },
      },
    },
    queryClient,
  );

  const rejectMutation = useRejectAccountInvitation(
    {
      mutation: {
        onSuccess: () => {
          message.success('Invitation declined');

          queryClient.invalidateQueries({ queryKey: getGetAccountQueryKey() });

          navigate({ to: '/' });
        },
        onError: () => {
          message.error('Failed to decline invitation');
        },
      },
    },
    queryClient,
  );

  const footer = <TextTertiary>{`© ${dayjs().year()} K3X Inc. Secure System.`}</TextTertiary>;

  if (!invitationFromAccount && previewLoading) {
    return (
      <CenteredLayout imageUrl="/logo.png" bottom={footer}>
        <CenteredLayoutContent title="Loading">
          <Flex justify="center" style={{ padding: '24px 0' }}>
            <Spin size="large" />
          </Flex>
        </CenteredLayoutContent>
      </CenteredLayout>
    );
  }

  if (!invitationFromAccount && previewError) {
    return (
      <CenteredLayout imageUrl="/logo.png" bottom={footer}>
        <CenteredLayoutContent
          title="Invitation unavailable"
          subtitle="This invitation does not exist or is no longer valid."
        >
          <Button type="primary" size="large" block onClick={() => navigate({ to: '/' })}>
            Go to Dashboard
          </Button>
        </CenteredLayoutContent>
      </CenteredLayout>
    );
  }

  if (!display) {
    return (
      <CenteredLayout imageUrl="/logo.png" bottom={footer}>
        <CenteredLayoutContent
          title="Invitation unavailable"
          subtitle="This invitation does not exist"
        >
          <Button type="primary" size="large" block onClick={() => navigate({ to: '/' })}>
            Go to Dashboard
          </Button>
        </CenteredLayoutContent>
      </CenteredLayout>
    );
  }

  const isMutationPending = acceptMutation.isPending || rejectMutation.isPending;

  return (
    <CenteredLayout imageUrl="/logo.png" bottom={footer}>
      <CenteredLayoutContent
        title={display.organization.name}
        width={520}
        subtitle={
          <span style={{ textAlign: 'center', display: 'block' }}>
            <Typography.Text>
              You were invited to join{' '}
              <Typography.Text strong ellipsis style={{ maxWidth: 220 }}>
                {display.organization.name}
              </Typography.Text>{' '}
              as{' '}
              <Typography.Text strong ellipsis style={{ maxWidth: 160 }}>
                {display.role.name}
              </Typography.Text>
              .
            </Typography.Text>
            {!canAct ? (
              <Typography.Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
                This invitation was sent to{' '}
                <Typography.Text strong>{display.email}</Typography.Text>. Sign in with that account
                to accept or decline.
              </Typography.Text>
            ) : null}
          </span>
        }
      >
        <Flex vertical align="center" gap={16}>
          {canAct ? (
            <Flex gap={16} wrap="wrap" justify="center">
              <Button
                size="large"
                color="default"
                variant="filled"
                loading={rejectMutation.isPending}
                disabled={isMutationPending}
                onClick={() => rejectMutation.mutate({ invitationId })}
              >
                Decline invitation
              </Button>
              <Button
                type="primary"
                size="large"
                loading={acceptMutation.isPending}
                disabled={isMutationPending}
                onClick={() => acceptMutation.mutate({ invitationId })}
              >
                Accept invitation
              </Button>
            </Flex>
          ) : (
            <Button
              type="primary"
              size="large"
              block
              onClick={async () => {
                await signOutWithRevoke();
                navigate({
                  to: '/auth/signin',
                  search: { redirectTo: `/invitation/${invitationId}` },
                });
              }}
            >
              Sign out and use another account
            </Button>
          )}
          <Button type="link" size="large" onClick={() => navigate({ to: '/' })}>
            Go to Dashboard
          </Button>
        </Flex>
      </CenteredLayoutContent>
    </CenteredLayout>
  );
};

export const Route = createFileRoute('/invitation/$invitationId')({
  beforeLoad: async ({ params, context }) => {
    hideHtmlLoader();

    const user = getAuth().currentUser;
    if (!user) {
      throw redirect({
        to: '/auth/signin',
        search: { redirectTo: `/invitation/${params.invitationId}` },
      });
    }

    const accountQuery = await context.queryClient.ensureQueryData(getGetAccountQueryOptions());
    if (!accountQuery.data) {
      throw redirect({ to: '/auth/signin' });
    }

    return { account: accountQuery.data };
  },
  component: InvitationRoute,
});
